/**
 * CreateApiKeyModal - Modal for creating new API keys
 *
 * Features:
 * - Name and description input
 * - Scope selection with categories
 * - Rate limit tier selection
 * - Optional expiration date
 * - IP whitelist (optional)
 * - Shows generated key after creation (ONLY ONCE!)
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Checkbox,
  Badge,
  Alert,
  AlertDescription,
  Spinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/ui';
import { Key, Copy, Check, AlertTriangle, Shield, Clock, Globe } from 'lucide-react';
import { createApiKey, ApiKeyCreateResponse, ApiKeyScope, RateLimitTier } from '@/services/apiKeyService';
import { API_KEY_SCOPE_DEFINITIONS, getScopeCategories } from '@simcheck/shared-types';

interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  onSuccess?: () => void;
}

type Step = 'form' | 'success';

export function CreateApiKeyModal({
  isOpen,
  onClose,
  organizationId,
  onSuccess,
}: CreateApiKeyModalProps) {
  // Step management
  const [step, setStep] = useState<Step>('form');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<ApiKeyScope[]>([]);
  const [rateLimitTier, setRateLimitTier] = useState<RateLimitTier>('standard');
  const [expiresIn, setExpiresIn] = useState<string>('never');
  const [allowedIPs, setAllowedIPs] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [copied, setCopied] = useState(false);

  // Scope categories
  const categories = getScopeCategories();

  const handleScopeToggle = useCallback((scope: ApiKeyScope) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  }, []);

  const handleCategoryToggle = useCallback(
    (category: string, allScopes: ApiKeyScope[]) => {
      const categoryScopes = allScopes.filter((s) =>
        API_KEY_SCOPE_DEFINITIONS.find((def) => def.scope === s && def.category === category)
      );
      const allSelected = categoryScopes.every((s) => selectedScopes.includes(s));

      if (allSelected) {
        setSelectedScopes((prev) => prev.filter((s) => !categoryScopes.includes(s)));
      } else {
        setSelectedScopes((prev) => [...new Set([...prev, ...categoryScopes])]);
      }
    },
    [selectedScopes]
  );

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (selectedScopes.length === 0) {
      setError('Please select at least one scope');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Calculate expiration date
      let expiresAt: Date | undefined;
      if (expiresIn !== 'never') {
        const now = new Date();
        switch (expiresIn) {
          case '30d':
            expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
            break;
          case '90d':
            expiresAt = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
            break;
          case '1y':
            expiresAt = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
            break;
        }
      }

      // Parse IP whitelist
      const ips = allowedIPs
        .split('\n')
        .map((ip) => ip.trim())
        .filter((ip) => ip.length > 0);

      const response = await createApiKey(
        {
          name: name.trim(),
          description: description.trim() || undefined,
          scopes: selectedScopes,
          rateLimitTier,
          expiresAt,
          allowedIPs: ips.length > 0 ? ips : undefined,
        },
        organizationId
      );

      setGeneratedKey(response.key);
      setStep('success');
      onSuccess?.();
    } catch (err: any) {
      console.error('Error creating API key:', err);
      setError(err.message || 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [generatedKey]);

  const handleClose = () => {
    // Reset state
    setStep('form');
    setName('');
    setDescription('');
    setSelectedScopes([]);
    setRateLimitTier('standard');
    setExpiresIn('never');
    setAllowedIPs('');
    setError('');
    setGeneratedKey('');
    setCopied(false);
    onClose();
  };

  // Render form step
  const renderForm = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Key className="w-5 h-5" />
          Create API Key
        </DialogTitle>
        <DialogDescription>
          Create a new API key for programmatic access to SimCheck.ai
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            placeholder="e.g., CI/CD Pipeline Key"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">
            A descriptive name to identify this key
          </p>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="Optional description..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading}
            rows={2}
          />
        </div>

        {/* Rate Limit Tier */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Rate Limit Tier
          </Label>
          <Select
            value={rateLimitTier}
            onValueChange={(v) => setRateLimitTier(v as RateLimitTier)}
            disabled={loading}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">
                Standard (60/min, 1,000/hr, 10,000/day)
              </SelectItem>
              <SelectItem value="elevated">
                Elevated (300/min, 5,000/hr, 50,000/day)
              </SelectItem>
              <SelectItem value="unlimited">Unlimited (no limits)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Expiration */}
        <div className="space-y-2">
          <Label>Expiration</Label>
          <Select value={expiresIn} onValueChange={setExpiresIn} disabled={loading}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="never">Never expires</SelectItem>
              <SelectItem value="30d">30 days</SelectItem>
              <SelectItem value="90d">90 days</SelectItem>
              <SelectItem value="1y">1 year</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* IP Whitelist */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            IP Whitelist (optional)
          </Label>
          <Textarea
            placeholder="Enter IP addresses, one per line..."
            value={allowedIPs}
            onChange={(e) => setAllowedIPs(e.target.value)}
            disabled={loading}
            rows={3}
          />
          <p className="text-xs text-muted-foreground">
            Leave empty to allow all IPs. Enter one IP per line.
          </p>
        </div>

        {/* Scopes */}
        <div className="space-y-4">
          <Label className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Permissions *
          </Label>
          <p className="text-xs text-muted-foreground">
            Select the permissions this API key will have
          </p>

          <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
            {categories.map((category) => {
              const scopeDefs = API_KEY_SCOPE_DEFINITIONS.filter(
                (def) => def.category === category
              );
              const allScopes = scopeDefs.map((def) => def.scope);
              const selectedInCategory = allScopes.filter((s) =>
                selectedScopes.includes(s)
              );
              const allSelected =
                scopeDefs.length > 0 && selectedInCategory.length === scopeDefs.length;
              const someSelected =
                selectedInCategory.length > 0 && selectedInCategory.length < scopeDefs.length;

              return (
                <div key={category} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`category-${category}`}
                      checked={allSelected}
                      className={someSelected ? 'data-[state=checked]:bg-muted-foreground' : ''}
                      onCheckedChange={() => handleCategoryToggle(category, allScopes)}
                      disabled={loading}
                    />
                    <Label
                      htmlFor={`category-${category}`}
                      className="font-medium capitalize cursor-pointer"
                    >
                      {category}
                    </Label>
                    {selectedInCategory.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {selectedInCategory.length}/{scopeDefs.length}
                      </Badge>
                    )}
                  </div>

                  <div className="ml-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {scopeDefs.map((def) => (
                      <div key={def.scope} className="flex items-start gap-2">
                        <Checkbox
                          id={`scope-${def.scope}`}
                          checked={selectedScopes.includes(def.scope)}
                          onCheckedChange={() => handleScopeToggle(def.scope)}
                          disabled={loading}
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor={`scope-${def.scope}`}
                            className="text-sm cursor-pointer flex items-center gap-1"
                          >
                            {def.displayName}
                            {def.isPrivileged && (
                              <Badge variant="outline" className="text-xs ml-1">
                                Privileged
                              </Badge>
                            )}
                          </Label>
                          <p className="text-xs text-muted-foreground">{def.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleCreate} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Creating...
            </>
          ) : (
            <>
              <Key className="w-4 h-4 mr-2" />
              Create API Key
            </>
          )}
        </Button>
      </DialogFooter>
    </>
  );

  // Render success step
  const renderSuccess = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-green-500">
          <Check className="w-5 h-5" />
          API Key Created
        </DialogTitle>
        <DialogDescription>
          Your new API key has been created successfully
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6 py-4">
        <Alert className="border-amber-500 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-amber-600 dark:text-amber-400">
            <strong>Important:</strong> Copy your API key now. You won't be able to see it again!
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label>Your API Key</Label>
          <div className="flex items-center gap-2">
            <div className="flex-1 p-3 bg-muted rounded-lg font-mono text-sm break-all border">
              {generatedKey}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopy}
              className="shrink-0"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Store this key securely. It will not be shown again.
          </p>
        </div>

        <div className="p-4 bg-muted/50 rounded-lg space-y-2">
          <h4 className="font-medium">Using your API key</h4>
          <p className="text-sm text-muted-foreground">
            Include this key in your API requests using the X-API-Key header:
          </p>
          <pre className="text-xs bg-background p-3 rounded border overflow-x-auto">
            {`curl -H "X-API-Key: ${generatedKey.substring(0, 20)}..." \\
     https://api.simcheck.ai/v1/flows`}
          </pre>
        </div>
      </div>

      <DialogFooter>
        <Button onClick={handleClose}>Done</Button>
      </DialogFooter>
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        {step === 'form' ? renderForm() : renderSuccess()}
      </DialogContent>
    </Dialog>
  );
}

export default CreateApiKeyModal;
